/*
 * Authors: Koen, mariushirjanu
 * Created: 31/01/2017
 * Upgraded: 08/01/2024
 */
import _ from 'underscore';
import { ChangeDetectorRef, Component, Input, OnInit, Type, ViewEncapsulation } from '@angular/core';
import { IOrderSearchResult, ISearchResults, OrderSearchDataService } from '@app/shared/services/order/order-search-data.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { StateService } from '@uirouter/angular';
import { IObject } from '@type_definitions/datatypes';
import { OrderSearchResultDetailComponent } from './order-search-result-detail.component';

interface IOrderSearchResultExtended extends IOrderSearchResult {
  expanded?:boolean;
}

@Component({
  selector: 'order-search',
  template: `
    <h2 translate>{{ title }}</h2>

    <search-input
      [searching]="searching || searchingMore"
      [query]="query"
      placeHolder="ORDERENTRY.SEARCH_ORDERS_QUOTES"
      searchTextRequired="true"
      (onSearch)="startSearch($event)"
    ></search-input>

    <search-results [query]="query" [results]="searchResults" [hideLinks]="hideLinks" [searching]="searching" [searchingMore]="searchingMore" [resultDetailComponent]="searchResultDetailComponent">

      <!-- pass content projection: no-results -->
      <div class="no-results ag-overlay-no-rows-center">
        <h3 translate>GA.GENERAL.NO_RESULTS_FOUND</h3>
        <div class="alert alert-warning">
          <h4 translate>ORDERENTRY.SEARCH.NO_RESULTS_INFO_TITLE</h4>
          <div class="search-help-text" translate>ORDERENTRY.SEARCH.NO_RESULTS_INFO_TEXT</div>
        </div>
      </div>

    </search-results>
  `,
  styleUrls: ['./order-search.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderSearchComponent implements OnInit {
  @Input() public query:string;
  public title:string;
  public searching:boolean;
  public searchingMore:boolean;
  public hideLinks:boolean;
  public searchResults:IOrderSearchResult[] = [];
  public readonly searchResultDetailComponent:Type<any> = OrderSearchResultDetailComponent;

  constructor(
    private orderSearchDataService:OrderSearchDataService,
    private stateService:StateService,
    private translationService:TranslationService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    this.title = this.translationService.instant('GA.GENERAL.SEARCH_LABEL', { label: 'GA.GENERAL.ORDERS' });
  }

  public ngOnInit():void {
    // when query parameter is filled -> doQuery to start searching
    if(this.query){
      this.doQuery(this.query);
    }
  }

  public startSearch(query:string):void {
    if(query === this.query){
      // handle case when the query is empty or not changed -> execute a new query + finish loading
      this.doQuery(this.query);
    } else {
      // redirect when a query changes
      this.stateService.go(this.stateService.current, { query });
    }
  }

  private doQuery(query:string):void {
    this.searchResults = [];
    this.searching = true;
    this.searchingMore = true;

    // start both search queries at the same time
    Promise.all([
      this.orderSearchDataService.search(query, true)
        .then(this.addResults)
        .finally(() => {
          this.searching = false;
        }),
      this.orderSearchDataService.search(query, false)
        .then(this.addResults)
        .finally(() => {
          this.searchingMore = false;
        })
    ]);
  }

  private addResults = (results:ISearchResults[]):void => {
    const orderResultSection = _.findWhere(results, { type: OrderSearchDataService.SEARCH_TYPE_ORDER });
    //remove search results that we already have
    const newSearchResults:IOrderSearchResultExtended[] = <IOrderSearchResultExtended[]>_.filter(orderResultSection.data, (orderSearchResult) => !_.findWhere(this.searchResults, { id: orderSearchResult.id }));

    _.each(newSearchResults, (searchResult:IOrderSearchResultExtended) => {
      searchResult.expanded = false;
    });

    //auto expand the first search result
    if (!this.searchResults.length && newSearchResults.length === 1) {
      newSearchResults[ 0 ].expanded = true;
    }

    this.searchResults = this.searchResults.concat(newSearchResults);
    this.hideLinks = orderResultSection.settings.hideLinks;
    this.changeDetectorRef.detectChanges();
  };

  public uiOnParamsChanged = (newValues:IObject):void => {
    if (newValues?.query) {
      this.query = newValues.query;
      this.doQuery(this.query);
    }
  };
}